import { useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Card, CardContent, TextField,
    Button, Box, Divider, Typography,
    IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemText,
    Link as MuiLink,
} from "@mui/material";
import { Visibility, VisibilityOff, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import api from "../../shared/api";
import FlashAlert from "../common/FlashAlert";

export default function PasswordResetForm({ viewModel, flash }) {
    const pr = viewModel.passwordRequirements;

    const schema = useMemo(() => {
        return z.object({
            currentPassword: viewModel.requireCurrentPassword
                ? z.string().min(1, "The current password field is required.")
                : z.string(),
            password: z.string().min(1, "The new password field is required."),
            passwordConfirmation: z.string().min(1, "The confirm new password field is required."),
        }).refine((data) => data.password === data.passwordConfirmation, {
            message: "The confirm new password field does not match.",
            path: ["passwordConfirmation"],
        }).refine((data) => {
            if (!viewModel.requireCurrentPassword) return true;
            return data.password !== data.currentPassword;
        }, {
            message: "The new password cannot be the same as your current password.",
            path: ["password"],
        }).refine((data) => {
            const pw = data.password;
            if (!pw) return true;
            const validChars = /^[A-Za-z0-9!#$%&()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/.test(pw);
            if (!validChars) return false;
            if (pw.length < pr.orgSPmin || pw.length > pr.orgSPmax) return false;
            if (pr.orgSPupper && !/[A-Z]/.test(pw)) return false;
            if (pr.orgSPlower && !/[a-z]/.test(pw)) return false;
            if (pr.orgSPnumber && !/[0-9]/.test(pw)) return false;
            if (pr.orgSPspecial && !/[!#$%&()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(pw)) return false;
            return true;
        }, {
            message: "The new password format is invalid.",
            path: ["password"],
        });
    }, [viewModel.requireCurrentPassword, pr]);

    const { control, handleSubmit, watch, setError, formState: { errors } } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { currentPassword: "", password: "", passwordConfirmation: "" },
    });

    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [pwVis, setPwVis] = useState(false);
    const [pwVisCurrent, setPwVisCurrent] = useState(false);

    const password = watch("password", "");

    const checks = [
        {
            met: password.length >= pr.orgSPmin && password.length <= pr.orgSPmax,
            label: pr.orgSPmin === pr.orgSPmax
                ? `password is ${pr.orgSPmin} characters long`
                : `password is ${pr.orgSPmin} to ${pr.orgSPmax} characters long`,
            show: true,
        },
        { met: /[A-Z]/.test(password), label: "contains an UPPERCASE character", show: !!pr.orgSPupper },
        { met: /[a-z]/.test(password), label: "contains a lowercase character", show: !!pr.orgSPlower },
        { met: /[0-9]/.test(password), label: "contains a number", show: !!pr.orgSPnumber },
        { met: /[!#$%&()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(password), label: "contains a special character", show: !!pr.orgSPspecial },
    ];

    const onSubmit = async (data) => {
        setFormError("");
        setLoading(true);
        try {
            const response = await api.post("/password-reset", data, { responseType: "json" });
            if (response.data.location) {
                window.location.replace(response.data.location);
                return;
            }
            if (!response.data.valid && response.data.errors) {
                response.data.errors.forEach((err) => {
                    if (err.PROPERTY.length === 0) {
                        setFormError((prev) => prev + " " + err.MESSAGE);
                    } else {
                        setError(err.PROPERTY, { message: err.MESSAGE });
                    }
                });
            }
        } catch {
            setFormError("There was a network error processing this request. Please contact support if this problem persists.");
        } finally {
            setLoading(false);
        }
    };

    const pwToggle = (
        <InputAdornment position="end">
            <IconButton onClick={() => setPwVis(!pwVis)} edge="end" size="small">
                {pwVis ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Card id="passwordReset" variant="elevation" elevation={0} square sx={{ border: "none" }}>
            <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5">Password Reset</Typography>
            </CardContent>

            <form onSubmit={handleSubmit(onSubmit)}>
                <CardContent>
                    <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                        <FlashAlert flash={flash} />

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Hello <strong>{viewModel.firstName}</strong>, please set a new password.
                        </Typography>

                        {viewModel.requireCurrentPassword && (
                            <>
                                <Controller
                                    name="currentPassword"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Current Password"
                                            type={pwVisCurrent ? "text" : "password"}
                                            autoFocus
                                            fullWidth
                                            error={!!errors.currentPassword}
                                            helperText={errors.currentPassword?.message}
                                            sx={{ mb: 3 }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setPwVisCurrent(!pwVisCurrent)} edge="end" size="small">
                                                            {pwVisCurrent ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Divider sx={{ mb: 3 }} />
                            </>
                        )}

                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="New Password"
                                    type={pwVis ? "text" : "password"}
                                    autoFocus={!viewModel.requireCurrentPassword}
                                    fullWidth
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                    sx={{ mb: 3 }}
                                    InputProps={{ endAdornment: pwToggle }}
                                />
                            )}
                        />

                        <Controller
                            name="passwordConfirmation"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Confirm New Password"
                                    type={pwVis ? "text" : "password"}
                                    fullWidth
                                    error={!!errors.passwordConfirmation}
                                    helperText={errors.passwordConfirmation?.message}
                                    sx={{ mb: 3 }}
                                    onKeyDown={(e) => e.key === "Enter" && handleSubmit(onSubmit)()}
                                />
                            )}
                        />

                        <List dense sx={{ listStyleType: "none", p: 0, mt: 1 }}>
                            {checks.filter((c) => c.show).map((check, i) => (
                                <ListItem key={i} disableGutters sx={{ py: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        {check.met
                                            ? <CheckCircle fontSize="small" color="success" />
                                            : <ErrorIcon fontSize="small" color="error" />
                                        }
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={check.label}
                                        primaryTypographyProps={{
                                            variant: "body2",
                                            color: check.met ? "success.main" : "error.main",
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                        {formError ? (
                            <Typography variant="subtitle1" color="error">{formError}</Typography>
                        ) : (
                            <Typography variant="subtitle1">&nbsp;</Typography>
                        )}
                    </Box>

                    <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={loading}
                            sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, py: 1.2 }}
                        >
                            {loading ? "Submitting..." : "Submit"}
                        </Button>
                    </Box>
                </CardContent>
            </form>

            <Divider />
            <CardContent>
                <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                    <MuiLink href="/login/forgot-username" display="block">Forgot my username</MuiLink>
                    <MuiLink href="/login" display="block">Back to login</MuiLink>
                </Box>
            </CardContent>
        </Card>
    );
}
